import { UserCredential } from "@firebase/auth";
import hasSessionStorage from "@mgdx/shared/src/utils/hasSessionStorage";
import { createErrorHandler } from "@mgdx-libs/error-handler/lib/createErrorHandler";
import { firebaseErrorHandler } from "@mgdx-libs/error-handler/lib/fireErrorHandler";
import { isFirebaseError } from "@mgdx-libs/error-handler/lib/isFirebaseError";
import { logger } from "@mgdx-libs/logger";
import { getIdToken } from "firebase/auth";

import { getFirebaseAuth } from "./getFirebaseAuth";

const ACCESS_TOKEN_STORAGE_KEY = "accessToken";
const errorHandlerReport = createErrorHandler();

const accessTokenSavedListeners = new Set<() => void>();

/**
 * トークンが保存されたときに呼ぶ関数を登録する。戻り値の関数で登録を解除する。
 */
export const subscribeAccessTokenSaved = (listener: () => void): (() => void) => {
  accessTokenSavedListeners.add(listener);
  return () => {
    accessTokenSavedListeners.delete(listener);
  };
};

export const getAccessToken = (): string => {
  if (hasSessionStorage()) {
    return sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) || "";
  }

  return "";
};

export const setAccessToken = (accessToken: string): void => {
  if (hasSessionStorage()) {
    sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
  }
  accessTokenSavedListeners.forEach((listener) => listener());
};

export const clearAccessToken = (): void => {
  if (hasSessionStorage()) {
    sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  }
};

/**
 * ID トークンを取得する。保存はしないため、保存するかどうかを呼び出し側で判断できる。
 */
export const fetchAccessToken = async (userCredential?: Pick<UserCredential, "user">): Promise<string | undefined> => {
  const firebaseAuth = getFirebaseAuth();
  const firebaseUser = userCredential ? userCredential.user : firebaseAuth?.currentUser;

  if (!firebaseUser) return undefined;

  logger.debug("@mgdx-libs/firebase fetchAccessToken User: %s", firebaseUser.uid);
  const accessToken = await getIdToken(firebaseUser).catch(async (errorOrFirebaseError) => {
    if (isFirebaseError(errorOrFirebaseError)) {
      try {
        firebaseErrorHandler(errorOrFirebaseError);
      } catch (error) {
        if (typeof error === "string" || error instanceof Error) {
          errorHandlerReport(error);
        } else {
          errorHandlerReport(new Error("Unknown error occurred"));
        }
      }
    } else {
      await errorHandlerReport(errorOrFirebaseError);
    }
  });

  logger.debug("@mgdx-libs/firebase fetchAccessToken getIdToken: %s", accessToken ? "obtained" : "none");
  return accessToken || undefined;
};

export const refreshAccessToken = async (
  userCredential?: Pick<UserCredential, "user">
): Promise<string | undefined> => {
  const accessToken = await fetchAccessToken(userCredential);
  if (accessToken) setAccessToken(accessToken);

  return accessToken;
};

// 保存済みのトークンの持ち主を判定するためだけに payload の sub(uid)を読む。署名は検証しない
const getTokenUid = (accessToken: string): string | undefined => {
  try {
    const payload = accessToken.split(".")[1];
    if (!payload) return undefined;

    const { sub } = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as { sub?: unknown };
    return typeof sub === "string" ? sub : undefined;
  } catch {
    return undefined;
  }
};

/**
 * 保存済みのトークンの持ち主の uid を返す。トークンがない場合は undefined。
 */
export const getAccessTokenUid = (): string | undefined => getTokenUid(getAccessToken());

/**
 * 送信前に有効期限を確認した ID トークンを返す。
 * スリープ復帰直後など SDK の自動更新が間に合わない場合も、期限切れのトークンを送らないようにする。
 * 保存済みのトークンがログイン中の操作者のものでない場合(消去中・操作者の切り替え中)は、
 * 保存済みのトークンの扱いを認証状態の監視側に任せ、そのまま返す。
 * 取得に失敗した場合も保存済みのトークンを返し、401 時の forceRefreshAccessToken に任せる。
 */
export const getValidAccessToken = async (): Promise<string> => {
  const firebaseUser = getFirebaseAuth()?.currentUser;
  const isOwnedByFirebaseUser = () =>
    !!firebaseUser &&
    getFirebaseAuth()?.currentUser?.uid === firebaseUser.uid &&
    getTokenUid(getAccessToken()) === firebaseUser.uid;

  if (!firebaseUser || !isOwnedByFirebaseUser()) return getAccessToken();

  const accessToken = await getIdToken(firebaseUser).catch(() => undefined);
  // 取得中にサインアウトや操作者の切り替えが起きた場合は、取得したトークンを保存しない
  if (!accessToken || !isOwnedByFirebaseUser()) return getAccessToken();

  setAccessToken(accessToken);
  return accessToken;
};

export const forceRefreshAccessToken = async (
  userCredential?: Pick<UserCredential, "user">
): Promise<string | undefined> => {
  const firebaseAuth = getFirebaseAuth();

  if (!userCredential && firebaseAuth) {
    await firebaseAuth.authStateReady();
  }

  const firebaseUser = userCredential ? userCredential.user : firebaseAuth?.currentUser;

  if (firebaseUser) {
    logger.debug("@mgdx-libs/firebase forceRefreshAccessToken User: %s", firebaseUser.uid);
    const accessToken = await getIdToken(firebaseUser, true).catch(async (errorOrFirebaseError) => {
      if (isFirebaseError(errorOrFirebaseError)) {
        try {
          firebaseErrorHandler(errorOrFirebaseError);
        } catch (error) {
          if (typeof error === "string" || error instanceof Error) {
            errorHandlerReport(error);
          } else {
            errorHandlerReport(new Error("Unknown error occurred"));
          }
        }
      } else {
        await errorHandlerReport(errorOrFirebaseError);
      }
    });

    logger.debug("@mgdx-libs/firebase forceRefreshAccessToken getIdToken: %s", accessToken ? "obtained" : "none");
    // 取り直し中に別の操作者へ替わった場合や、サインアウトでトークンが消された場合は、前の操作者のトークンを保存しない。
    // 別タブの起動で一時的に未ログインになっただけなら、保存済みのトークンは残っているため保存する
    if (!userCredential) {
      const currentUid = firebaseAuth?.currentUser?.uid;
      const isSwitched = !!currentUid && currentUid !== firebaseUser.uid;
      const isSignedOut = !currentUid && getTokenUid(getAccessToken()) !== firebaseUser.uid;
      if (isSwitched || isSignedOut) return undefined;
    }

    if (accessToken) {
      setAccessToken(accessToken);
      return accessToken;
    }
  } else {
    logger.debug("@mgdx-libs/firebase forceRefreshAccessToken User is not signed in");
  }

  return undefined;
};
